import { useEffect, useState } from "react";
import { Star } from "lucide-react";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Real-customer testimonials wall. Pulls from /api/reviews/public (which
 * returns only owner-approved reviews) and emits AggregateRating JSON-LD
 * for Google's golden-stars SERP feature.
 *
 * If no reviews are loaded yet, shows nothing — never invents content.
 */
export default function TestimonialsWall({ limit = 6 }) {
  const [data, setData] = useState({ items: [], count: 0, average: null });
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/api/reviews/public?limit=${limit}`);
        const d = r.ok ? await r.json() : null;
        if (!cancelled && d) setData(d);
      } catch { /* noop */ }
    })();
    return () => { cancelled = true; };
  }, [limit]);

  // Emit AggregateRating schema — eligible for golden stars in Google SERPs.
  useEffect(() => {
    if (!data.count || !data.average) return;
    const ld = {
      "@context": "https://schema.org",
      "@type": "Product",
      "name": "Central Rentals Canada",
      "description": "Canadian property management software",
      "brand": { "@type": "Brand", "name": "Central Rentals Canada" },
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": String(data.average),
        "reviewCount": data.count,
        "bestRating": "5",
        "worstRating": "1",
      },
      "review": data.items.slice(0, 6).map((r) => ({
        "@type": "Review",
        "author": { "@type": "Person", "name": r.name },
        "reviewRating": { "@type": "Rating", "ratingValue": String(r.rating), "bestRating": "5" },
        "reviewBody": r.text,
      })),
    };
    let script = document.head.querySelector('script[data-reviews-jsonld="1"]');
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.setAttribute("data-reviews-jsonld", "1");
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ld);
    return () => { if (script) script.remove(); };
  }, [data]);

  if (!data.count) return null;

  const items = data.items.slice(0, limit);
  const initials = (n) => n.split(" ").filter(Boolean).slice(0, 2).map((p) => p[0]).join("").toUpperCase();

  return (
    <section className="max-w-6xl mx-auto px-5 md:px-8 py-14" data-testid="testimonials-wall">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-emerald-700">Customer reviews</div>
          <h2 className="font-display text-2xl md:text-3xl font-extrabold tracking-tighter text-slate-900 mt-1">
            {data.average} / 5 from {data.count} Canadian landlord{data.count === 1 ? "" : "s"}
          </h2>
        </div>
        <div className="flex items-center gap-1">
          {[1,2,3,4,5].map((i) => (
            <Star key={i} className={`w-5 h-5 ${data.average && i <= Math.round(data.average) ? "fill-amber-400 text-amber-400" : "text-slate-300"}`}/>
          ))}
        </div>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((r) => (
          <div key={r.id || `${r.name}-${r.text.slice(0, 20)}`} className="p-5 rounded-2xl border border-slate-200 bg-white" data-testid={`testimonial-${r.id || ''}`}>
            <div className="flex items-center gap-1 text-amber-400 mb-3">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? "fill-current" : "text-slate-200"}`}/>
              ))}
            </div>
            <blockquote className="text-sm text-slate-700 leading-relaxed">&ldquo;{r.text}&rdquo;</blockquote>
            <div className="mt-4 flex items-center gap-3 pt-4 border-t border-slate-100">
              {r.avatar_url ? (
                <img src={r.avatar_url} alt={r.name} loading="lazy" className="w-9 h-9 rounded-full object-cover"/>
              ) : (
                <div className="w-9 h-9 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">{initials(r.name)}</div>
              )}
              <div>
                <div className="text-sm font-bold text-slate-900">{r.name}</div>
                <div className="text-xs text-slate-500">{[r.role, r.location].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
